import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Tabs,
  Tab,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Divider,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  BarChart as BarChartIcon,
  ShowChart as ShowChartIcon,
  PieChart as PieChartIcon,
  TrendingUp as TrendingUpIcon,
  Timeline as TimelineIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  AccountBalance as AccountBalanceIcon,
  Public as PublicIcon,
  LocationOn as LocationOnIcon,
  CalendarToday as CalendarIcon
} from "@mui/icons-material";
import { format } from "date-fns";
import { ExecutePostWithParams, GetDataFromApiWithParams } from "../../../../services/api/ExecuteApiRequests";

// Helper function to normalize currency values
const normalizeCurrency = (paymentMode) => {
  if (!paymentMode) return "unknown";
  const mode = paymentMode?.toLowerCase() || "";
  if (mode === "usd" || mode === "$") return "usd";
  if (mode === "birr" || mode === "etb" || mode === "br") return "etb";
  return mode;
};

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`investment-tabpanel-${index}`}
      aria-labelledby={`investment-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Utility Functions
const formatCurrencyUSD = (amount) => {
  if (!amount && amount !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatCurrencyETB = (amount) => {
  if (!amount && amount !== 0) return "Br0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'ETB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatNumber = (number) => {
  if (!number && number !== 0) return "0";
  return new Intl.NumberFormat('en-US').format(number);
};

// Yearly Line Chart Component
const YearlyLineChart = ({ title, yearlyData, dataKey, color, formatValue }) => {
  const years = Object.keys(yearlyData).sort((a, b) => a - b);
  
  if (years.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          No yearly data available
        </Typography>
      </Box>
    );
  }
  
  const maxValue = Math.max(...Object.values(yearlyData).map(d => d[dataKey] || 0), 0);
  const chartHeight = 350;
  const chartWidth = Math.max(600, years.length * 100);
  const padding = { top: 30, right: 40, bottom: 60, left: 70 };
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>
        {title}
      </Typography>
      
      <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
        {[0, 25, 50, 75, 100].map((percent) => {
          const y = chartHeight - padding.bottom - (percent / 100) * (chartHeight - padding.top - padding.bottom);
          const value = (maxValue * percent) / 100;
          return (
            <g key={percent}>
              <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} stroke="#e0e0e0" strokeWidth="1" strokeDasharray="4" />
              <text x={padding.left - 8} y={y} textAnchor="end" fontSize="10" fill="#666">{formatValue(value)}</text>
            </g>
          );
        })}
        
        <polyline
          points={years.map((year, index) => {
            const x = padding.left + (index / (years.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
            const revenueValue = yearlyData[year][dataKey] || 0;
            const y = chartHeight - padding.bottom - (revenueValue / maxValue) * (chartHeight - padding.top - padding.bottom);
            return `${x},${y}`;
          }).join(' ')}
          fill="none"
          stroke={color}
          strokeWidth="2.5"
        />
        
        {years.map((year, index) => {
          const x = padding.left + (index / (years.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
          const revenueValue = yearlyData[year][dataKey] || 0;
          const y = chartHeight - padding.bottom - (revenueValue / maxValue) * (chartHeight - padding.top - padding.bottom);
          
          return (
            <g key={year}>
              <Tooltip title={`Year: ${year}\n${formatValue(revenueValue)}`} arrow placement="top">
                <circle cx={x} cy={y} r="6" fill={color} stroke="white" strokeWidth="2" style={{ cursor: 'pointer' }} />
              </Tooltip>
              <text x={x} y={chartHeight - padding.bottom + 20} textAnchor="middle" fontSize="11" fontWeight="bold">{year}</text>
              <text x={x} y={y - 8} textAnchor="middle" fontSize="9" fill="#666">{formatValue(revenueValue)}</text>
            </g>
          );
        })}
        
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={chartHeight - padding.bottom} stroke="#999" strokeWidth="1.5" />
        <line x1={padding.left} y1={chartHeight - padding.bottom} x2={chartWidth - padding.right} y2={chartHeight - padding.bottom} stroke="#999" strokeWidth="1.5" />
        <text x={20} y={chartHeight / 2} textAnchor="middle" fontSize="11" fill="#666" transform={`rotate(-90, 20, ${chartHeight / 2})`}>Amount</text>
        <text x={chartWidth / 2} y={chartHeight - 10} textAnchor="middle" fontSize="11" fill="#666">Year</text>
      </svg>
      
      <Box sx={{ width: '100%', bgcolor: '#f5f5f5', mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">Years: {years.join(' → ')}</Typography>
      </Box>
    </Box>
  );
};

// Yearly Bar Chart Component
const YearlyBarChart = ({ title, yearlyData, dataKey, color, formatValue }) => {
  const years = Object.keys(yearlyData).sort((a, b) => a - b);
  
  if (years.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No yearly data available</Typography>
      </Box>
    );
  }
  
  const maxValue = Math.max(...Object.values(yearlyData).map(d => d[dataKey] || 0), 0);
  const barWidth = Math.min(50, Math.max(30, 80 - years.length * 2));
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>{title}</Typography>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-end', 
        gap: 2.5, 
        minHeight: 380, 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        padding: 2
      }}>
        {years.map((year) => {
          const value = yearlyData[year][dataKey] || 0;
          const height = maxValue > 0 ? (value / maxValue) * 280 : 0;
          const percentage = maxValue > 0 ? ((value / maxValue) * 100).toFixed(1) : 0;
          
          return (
            <Tooltip 
              key={year} 
              title={`Year: ${year}\nValue: ${formatValue(value)}\nPercentage: ${percentage}% of max`} 
              arrow 
              placement="top"
            >
              <Box sx={{ 
                textAlign: 'center', 
                minWidth: 70,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}>
                <Box sx={{ 
                  position: 'relative',
                  height: height,
                  width: `${barWidth}px`,
                  bgcolor: color,
                  borderRadius: '4px 4px 0 0',
                  margin: '0 auto',
                  transition: 'all 0.3s ease',
                  opacity: 0.85,
                  '&:hover': {
                    opacity: 1,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                  }
                }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      position: 'absolute', 
                      top: -22, 
                      left: '50%', 
                      transform: 'translateX(-50%)',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: color,
                      whiteSpace: 'nowrap',
                      display: height > 35 ? 'none' : 'block'
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                </Box>
                
                {height > 35 && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: 'white',
                      mt: -2.5,
                      mb: 0.5,
                      position: 'relative',
                      zIndex: 1,
                      textShadow: '0px 0px 2px rgba(0,0,0,0.5)'
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                )}
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 1.5, 
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                    color: '#333'
                  }}
                >
                  {year}
                </Typography>
                
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block', 
                    color: '#888',
                    fontSize: '0.65rem',
                    mt: 0.25
                  }}
                >
                  ({percentage}%)
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      
      <Box sx={{ 
        width: '100%', 
        bgcolor: '#f8f9fa', 
        mt: 3, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        p: 2, 
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
          Years: {years.length} | Range: {years[0]} - {years[years.length - 1]} | Max: {formatValue(maxValue)}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.7rem', mt: 0.5 }}>
          Total: {formatValue(Object.values(yearlyData).reduce((sum, d) => sum + (d[dataKey] || 0), 0))}
        </Typography>
      </Box>
    </Box>
  );
};

// Park Bar Chart Component
const ParkBarChart = ({ title, parkData, dataKey, color, formatValue }) => {
  const parks = Object.keys(parkData).sort();
  
  if (parks.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No park data available</Typography>
      </Box>
    );
  }
  
  const maxValue = Math.max(...Object.values(parkData).map(d => d[dataKey] || 0), 0);
  
  if (maxValue === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available for {title}</Typography>
      </Box>
    );
  }
  
  const barWidth = Math.min(40, Math.max(25, 60 - parks.length));
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>{title}</Typography>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-end', 
        gap: 2, 
        minHeight: 400, 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        padding: 2
      }}>
        {parks.map((park) => {
          const value = parkData[park][dataKey] || 0;
          const height = maxValue > 0 ? (value / maxValue) * 280 : 0;
          const percentage = maxValue > 0 ? ((value / maxValue) * 100).toFixed(1) : 0;
          
          return (
            <Tooltip 
              key={park} 
              title={`${park}: ${formatValue(value)} (${percentage}% of max)`} 
              arrow 
              placement="top"
            >
              <Box sx={{ 
                textAlign: 'center', 
                minWidth: 70,
                maxWidth: 100,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-8px)'
                }
              }}>
                <Box sx={{ 
                  position: 'relative',
                  height: height,
                  width: `${barWidth}px`,
                  bgcolor: color,
                  borderRadius: '3px 3px 0 0',
                  margin: '0 auto',
                  transition: 'all 0.3s ease',
                  opacity: 0.85,
                  '&:hover': {
                    opacity: 1,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }
                }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      position: 'absolute', 
                      top: -20, 
                      left: '50%', 
                      transform: 'translateX(-50%)',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: color,
                      whiteSpace: 'nowrap',
                      display: height > 30 ? 'none' : 'block'
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                </Box>
                
                {height > 30 && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: 'white',
                      mt: -2.5,
                      mb: 0.5,
                      position: 'relative',
                      zIndex: 1,
                      textShadow: '0px 0px 2px rgba(0,0,0,0.5)'
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                )}
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 1, 
                    fontWeight: 'bold', 
                    fontSize: '0.7rem',
                    maxWidth: '100px',
                    wordWrap: 'break-word',
                    lineHeight: 1.2,
                    color: '#555'
                  }}
                >
                  {park.length > 20 ? park.substring(0, 18) + '...' : park}
                </Typography>
                
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block', 
                    color: '#999',
                    fontSize: '0.65rem',
                    mt: 0.25
                  }}
                >
                  ({percentage}%)
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      
      <Box sx={{ 
        width: '100%', 
        bgcolor: '#f8f9fa', 
        mt: 3, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        p: 2, 
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
          Total Parks: {parks.length} | Max Value: {formatValue(maxValue)} | Total: {formatValue(Object.values(parkData).reduce((sum, d) => sum + (d[dataKey] || 0), 0))}
        </Typography>
      </Box>
    </Box>
  );
};

// Pie Chart Component - FIXED (corrected fontSize syntax)
const PieChart = ({ title, data, formatValue }) => {
  const items = Object.keys(data);
  const total = items.reduce((sum, key) => sum + (data[key] || 0), 0);
  const colors = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f', '#0288d1', '#7b1fa2', '#e65100', '#c2185b', '#00695c'];
  
  if (items.length === 0 || total === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available for the selected filters</Typography>
      </Box>
    );
  }
  
  let startAngle = 0;
  const centerX = 120, centerY = 120, radius = 90;
  const svgSize = 280;
  
  const getPercentage = (value) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : 0;
  };
  
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 2, textAlign: 'center' }}>
        {title} - Total: {formatValue(total)}
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ position: 'relative', width: svgSize, height: svgSize }}>
          <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
            {items.map((key, index) => {
              const value = data[key] || 0;
              const percentage = total > 0 ? (value / total) * 100 : 0;
              const angle = (percentage / 100) * 360;
              const endAngle = startAngle + angle;
              
              const startRad = (startAngle * Math.PI) / 180;
              const endRad = (endAngle * Math.PI) / 180;
              
              const x1 = centerX + radius * Math.cos(startRad);
              const y1 = centerY + radius * Math.sin(startRad);
              const x2 = centerX + radius * Math.cos(endRad);
              const y2 = centerY + radius * Math.sin(endRad);
              
              const largeArcFlag = angle > 180 ? 1 : 0;
              const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
              
              const slice = (
                <path 
                  key={key} 
                  d={pathData} 
                  fill={colors[index % colors.length]} 
                  stroke="white" 
                  strokeWidth="2"
                  style={{ cursor: 'pointer', transition: 'opacity 0.3s' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                />
              );
              startAngle = endAngle;
              return slice;
            })}
            
            <circle cx={centerX} cy={centerY} r="25" fill="white" />
            <text x={centerX} y={centerY - 5} textAnchor="middle" fontSize="11" fontWeight="bold" fill="#333">Total</text>
            <text x={centerX} y={centerY + 12} textAnchor="middle" fontSize="12" fontWeight="bold" fill="#1976d2">{formatValue(total)}</text>
          </svg>
        </Box>
        
        <Box sx={{ minWidth: 200, maxHeight: 250, overflowY: 'auto' }}>
          {items.map((key, index) => {
            const value = data[key] || 0;
            const percentage = getPercentage(value);
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: colors[index % colors.length], borderRadius: 1 }} />
                <Typography variant="caption" sx={{ flex: 1 }}>
                  {key.length > 20 ? key.substring(0, 18) + '...' : key} ({percentage}%)
                </Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ color: colors[index % colors.length] }}>
                  {formatValue(value)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      
      <Box sx={{ width: '100%', bgcolor: '#f5f5f5', mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">Total Items: {items.length}</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>Total Value: {formatValue(total)}</Typography>
      </Box>
    </Box>
  );
};

// Capital Park Bar Chart Component (USD vs ETB together)
const CapitalParkBarChart = ({ title, parkData }) => {
  const parks = Object.keys(parkData).sort();
  
  if (parks.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No park data available</Typography>
      </Box>
    );
  }
  
  const maxValueUSD = Math.max(...Object.values(parkData).map(d => d.totalCapitalUSD || 0), 0);
  const maxValueETB = Math.max(...Object.values(parkData).map(d => d.totalCapitalETB || 0), 0);
  const maxValue = Math.max(maxValueUSD, maxValueETB);
  
  if (maxValue === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No capital data available for {title}</Typography>
      </Box>
    );
  }
  
  const barWidth = Math.min(30, Math.max(20, 45 - parks.length));
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>{title}</Typography>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-end', 
        gap: 3, 
        minHeight: 450, 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        padding: 2
      }}>
        {parks.map((park) => {
          const valueUSD = parkData[park]?.totalCapitalUSD || 0;
          const valueETB = parkData[park]?.totalCapitalETB || 0;
          const heightUSD = maxValue > 0 ? (valueUSD / maxValue) * 280 : 0;
          const heightETB = maxValue > 0 ? (valueETB / maxValue) * 280 : 0;
          const totalAmount = valueUSD + valueETB;
          const usdPercentage = totalAmount > 0 ? (valueUSD / totalAmount) * 100 : 0;
          const etbPercentage = totalAmount > 0 ? (valueETB / totalAmount) * 100 : 0;
          
          return (
            <Tooltip 
              key={park} 
              title={
                <Box>
                  <Typography variant="subtitle2">{park}</Typography>
                  <Typography variant="body2">USD: {formatCurrencyUSD(valueUSD)} ({usdPercentage.toFixed(1)}%)</Typography>
                  <Typography variant="body2">ETB: {formatCurrencyETB(valueETB)} ({etbPercentage.toFixed(1)}%)</Typography>
                  <Typography variant="body2">Total: {formatCurrencyUSD(valueUSD)} + {formatCurrencyETB(valueETB)}</Typography>
                </Box>
              } 
              arrow 
              placement="top"
            >
              <Box sx={{ 
                textAlign: 'center', 
                minWidth: 90,
                maxWidth: 120,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-8px)'
                }
              }}>
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'flex-end', height: 320 }}>
                  {/* USD Bar */}
                  <Box sx={{ 
                    position: 'relative',
                    height: heightUSD,
                    width: `${barWidth}px`,
                    bgcolor: '#1976d2',
                    borderRadius: '3px 3px 0 0',
                    transition: 'all 0.3s ease',
                    opacity: 0.85,
                    '&:hover': {
                      opacity: 1,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }
                  }}>
                    {heightUSD > 30 && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          position: 'absolute',
                          bottom: heightUSD - 20,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '9px',
                          fontWeight: 'bold',
                          color: 'white',
                          whiteSpace: 'nowrap',
                          textShadow: '0px 0px 2px rgba(0,0,0,0.5)'
                        }}
                      >
                        {formatCurrencyUSD(valueUSD)}
                      </Typography>
                    )}
                  </Box>
                  
                  {/* ETB Bar */}
                  <Box sx={{ 
                    position: 'relative',
                    height: heightETB,
                    width: `${barWidth}px`,
                    bgcolor: '#ed6c02',
                    borderRadius: '3px 3px 0 0',
                    transition: 'all 0.3s ease',
                    opacity: 0.85,
                    '&:hover': {
                      opacity: 1,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }
                  }}>
                    {heightETB > 30 && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          position: 'absolute',
                          bottom: heightETB - 20,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '9px',
                          fontWeight: 'bold',
                          color: 'white',
                          whiteSpace: 'nowrap',
                          textShadow: '0px 0px 2px rgba(0,0,0,0.5)'
                        }}
                      >
                        {formatCurrencyETB(valueETB)}
                      </Typography>
                    )}
                  </Box>
                </Box>
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 1, 
                    fontWeight: 'bold', 
                    fontSize: '0.7rem',
                    maxWidth: '100px',
                    wordWrap: 'break-word',
                    lineHeight: 1.2,
                    color: '#555'
                  }}
                >
                  {park.length > 20 ? park.substring(0, 18) + '...' : park}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: '#1976d2', borderRadius: 1 }} />
                    <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>USD</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: '#ed6c02', borderRadius: 1 }} />
                    <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>ETB</Typography>
                  </Box>
                </Box>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      
      <Box sx={{ 
        width: '100%', 
        bgcolor: '#f8f9fa', 
        mt: 3, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        p: 2, 
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
          Total Parks: {parks.length}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.7rem', mt: 0.5 }}>
          Total USD: {formatCurrencyUSD(Object.values(parkData).reduce((sum, d) => sum + (d.totalCapitalUSD || 0), 0))} | 
          Total ETB: {formatCurrencyETB(Object.values(parkData).reduce((sum, d) => sum + (d.totalCapitalETB || 0), 0))}
        </Typography>
      </Box>
    </Box>
  );
};

// Yearly Combined Capital Chart Component (USD + ETB together)
const YearlyCombinedCapitalChart = ({ title, yearlyData }) => {
    const years = Object.keys(yearlyData).sort((a, b) => a - b);
    
    if (years.length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            No capital data available
          </Typography>
        </Box>
      );
    }
    
    const maxValueUSD = Math.max(...Object.values(yearlyData).map(d => d.totalCapitalUSD || 0), 0);
    const maxValueETB = Math.max(...Object.values(yearlyData).map(d => d.totalCapitalETB || 0), 0);
    const maxValue = Math.max(maxValueUSD, maxValueETB);
    const chartHeight = 350;
    const chartWidth = Math.max(600, years.length * 100);
    const padding = { top: 30, right: 40, bottom: 60, left: 80 };
    
    if (maxValue === 0) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">No capital data available</Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ p: 2, overflowX: 'auto' }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>
          {title}
        </Typography>
        
        <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
          {/* Grid lines */}
          {[0, 25, 50, 75, 100].map((percent) => {
            const y = chartHeight - padding.bottom - (percent / 100) * (chartHeight - padding.top - padding.bottom);
            const value = (maxValue * percent) / 100;
            return (
              <g key={percent}>
                <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} stroke="#e0e0e0" strokeWidth="1" strokeDasharray="4" />
                <text x={padding.left - 8} y={y} textAnchor="end" fontSize="10" fill="#666">
                  {formatCurrencyUSD(value)}
                </text>
              </g>
            );
          })}
          
          {/* USD Line */}
          <polyline
            points={years.map((year, index) => {
              const x = padding.left + (index / (years.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
              const value = yearlyData[year]?.totalCapitalUSD || 0;
              const y = chartHeight - padding.bottom - (value / maxValue) * (chartHeight - padding.top - padding.bottom);
              return `${x},${y}`;
            }).join(' ')}
            fill="none"
            stroke="#1976d2"
            strokeWidth="2.5"
          />
          
          {/* ETB Line */}
          <polyline
            points={years.map((year, index) => {
              const x = padding.left + (index / (years.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
              const value = yearlyData[year]?.totalCapitalETB || 0;
              const y = chartHeight - padding.bottom - (value / maxValue) * (chartHeight - padding.top - padding.bottom);
              return `${x},${y}`;
            }).join(' ')}
            fill="none"
            stroke="#ed6c02"
            strokeWidth="2.5"
          />
          
          {/* Data points and labels */}
          {years.map((year, index) => {
            const x = padding.left + (index / (years.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
            const usdValue = yearlyData[year]?.totalCapitalUSD || 0;
            const etbValue = yearlyData[year]?.totalCapitalETB || 0;
            const yUSD = chartHeight - padding.bottom - (usdValue / maxValue) * (chartHeight - padding.top - padding.bottom);
            const yETB = chartHeight - padding.bottom - (etbValue / maxValue) * (chartHeight - padding.top - padding.bottom);
            
            return (
              <g key={year}>
                {/* USD Point */}
                <circle cx={x} cy={yUSD} r="6" fill="#1976d2" stroke="white" strokeWidth="2" style={{ cursor: 'pointer' }} />
                <text x={x} y={yUSD - 8} textAnchor="middle" fontSize="9" fill="#1976d2">{formatCurrencyUSD(usdValue)}</text>
                
                {/* ETB Point */}
                <circle cx={x} cy={yETB} r="6" fill="#ed6c02" stroke="white" strokeWidth="2" style={{ cursor: 'pointer' }} />
                <text x={x} y={yETB + 18} textAnchor="middle" fontSize="9" fill="#ed6c02">{formatCurrencyETB(etbValue)}</text>
                
                {/* Year Label */}
                <text x={x} y={chartHeight - padding.bottom + 20} textAnchor="middle" fontSize="11" fontWeight="bold">{year}</text>
              </g>
            );
          })}
          
          {/* Axes */}
          <line x1={padding.left} y1={padding.top} x2={padding.left} y2={chartHeight - padding.bottom} stroke="#999" strokeWidth="1.5" />
          <line x1={padding.left} y1={chartHeight - padding.bottom} x2={chartWidth - padding.right} y2={chartHeight - padding.bottom} stroke="#999" strokeWidth="1.5" />
          
          {/* Y-Axis Label */}
          <text x={20} y={chartHeight / 2} textAnchor="middle" fontSize="11" fill="#666" transform={`rotate(-90, 20, ${chartHeight / 2})`}>Amount</text>
          
          {/* X-Axis Label */}
          <text x={chartWidth / 2} y={chartHeight - 10} textAnchor="middle" fontSize="11" fill="#666">Year</text>
        </svg>
        
        {/* Legend */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 3, bgcolor: '#1976d2', borderRadius: 1 }} />
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>USD Capital</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 3, bgcolor: '#ed6c02', borderRadius: 1 }} />
            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>ETB (Birr) Capital</Typography>
          </Box>
        </Box>
        
        <Box sx={{ width: '100%', bgcolor: '#f5f5f5', mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, borderRadius: 1 }}>
          <Typography variant="body2" color="textSecondary">Years: {years.join(' → ')}</Typography>
        </Box>
      </Box>
    );
  };

export default function InvestmentReportDashboard() {
  // State Management
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPark, setSelectedPark] = useState("all");
  const [selectedInvestmentCategory, setSelectedInvestmentCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedRentType, setSelectedRentType] = useState("all"); // NEW: Rent Type filter state
  const [startYear, setStartYear] = useState("all");
  const [endYear, setEndYear] = useState("all");
  const [startMonth, setStartMonth] = useState("all");
  const [endMonth, setEndMonth] = useState("all");
  
  // Chart States
  const [showChart, setShowChart] = useState(false);
  const [chartView, setChartView] = useState("park");
  const [parkChartType, setParkChartType] = useState('bar');
  const [yearlyChartType, setYearlyChartType] = useState('bar');
  const [selectedParkForYearly, setSelectedParkForYearly] = useState("all");
  const [parkChartTab, setParkChartTab] = useState(0);
  const [yearlyChartTab, setYearlyChartTab] = useState(0);
  
  // Data States
  const [contractsData, setContractsData] = useState([]);
  const [investorsData, setInvestorsData] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [filteredInvestors, setFilteredInvestors] = useState([]);
  
  // Dynamic filter options (populated from data)
  const [parksList, setParksList] = useState(["all"]);
  const [investmentCategoriesList, setInvestmentCategoriesList] = useState(["all"]);
  const [statusesList, setStatusesList] = useState(["all"]);
  const [rentTypesList, setRentTypesList] = useState(["all"]); // NEW: Rent types list from contracts
  
  // Dialog States
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailsType, setDetailsType] = useState("");

  // Generate last 10 years dynamically
  const getLastNYears = (n) => {
    const currentYear = new Date().getFullYear();
    const years = ["all"];
    for (let i = 0; i < n; i++) {
      years.push((currentYear - i).toString());
    }
    return years;
  };
  
  const years = getLastNYears(10);
  const months = [
    { value: "all", label: "All Months" },
    { value: "01", label: "January" }, { value: "02", label: "February" },
    { value: "03", label: "March" }, { value: "04", label: "April" },
    { value: "05", label: "May" }, { value: "06", label: "June" },
    { value: "07", label: "July" }, { value: "08", label: "August" },
    { value: "09", label: "September" }, { value: "10", label: "October" },
    { value: "11", label: "November" }, { value: "12", label: "December" }
  ];

  const getInvestmentCategoryColor = (category) => {
    switch(category) {
      case 'Foreign': return { bg: '#e3f2fd', color: '#1565c0' };
      case 'Local': return { bg: '#e8f5e9', color: '#2e7d32' };
      default: return { bg: '#f5f5f5', color: '#616161' };
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch(statusLower) {
      case 'active': return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'pending renewal': return { bg: '#fff3e0', color: '#ef6c00' };
      case 'expired': return { bg: '#ffebee', color: '#c62828' };
      case 'inactive': return { bg: '#f5f5f5', color: '#9e9e9e' };
      case 'approved': return { bg: '#e3f2fd', color: '#1565c0' };
      case 'rejected': return { bg: '#ffebee', color: '#c62828' };
      default: return { bg: '#f5f5f5', color: '#616161' };
    }
  };

  // Transform API data to expected format and extract unique filters
  const transformContractData = (contracts, investors) => {
    const uniqueParksSet = new Set();
    const uniqueInvestmentCategoriesSet = new Set();
    const uniqueStatusesSet = new Set();
    const uniqueRentTypesSet = new Set(); // NEW: Collect unique rent types
    
    const transformedContracts = contracts.map(contract => {
      const investor = investors.find(i => i.id === contract.tenant_id || i.investor_id === contract.tenant_id);
      let parkName = contract.park_name || contract.park?.name || "N/A";
      
      if (parkName && parkName !== "N/A") {
        uniqueParksSet.add(parkName);
      }
      
      let investmentType = contract.investment_type || contract.investment_category || "N/A";
      if (investmentType && investmentType !== "N/A") {
        uniqueInvestmentCategoriesSet.add(investmentType);
      }
      
      let status = contract.status || "Active";
      if (status && status !== "N/A") {
        uniqueStatusesSet.add(status);
      }
      
      // NEW: Collect unique ownership_type values
      let rentType = contract.ownership_type || "N/A";
      if (rentType && rentType !== "N/A") {
        uniqueRentTypesSet.add(rentType);
      }
      
      return {
        ...contract,
        company_name: investor?.company_name || contract.company_name || "N/A",
        park_name: parkName,
        year: contract.year || (contract.contract_issued_on ? new Date(contract.contract_issued_on).getFullYear().toString() : "2024"),
        month: contract.month || (contract.contract_issued_on ? (new Date(contract.contract_issued_on).getMonth() + 1).toString().padStart(2, '0') : "01"),
        contact_person: investor?.contact_person || "N/A",
        phone_no: investor?.phone_no || "N/A",
        email: investor?.email || "N/A",
        nationality_origin: investor?.nationality_origin || "N/A",
        capital: investor?.capital || 0,
        payment_mode: contract.payment_mode || "USD",
        status: status,
        investment_category: investmentType,
        ownership_type: contract.ownership_type || "N/A" // NEW: Preserve ownership_type
      };
    });
    
    return { transformedContracts, uniqueParksSet, uniqueInvestmentCategoriesSet, uniqueStatusesSet, uniqueRentTypesSet };
  };

  const transformInvestorData = (investors, contracts) => {
    const uniqueParksSet = new Set();
    const uniqueStatusesSet = new Set();
    
    const transformedInvestors = investors.map(investor => {
      const contract = contracts.find(c => 
        c.tenant_id === investor.id || 
        c.tenant_id === investor.investor_id ||
        c.investor_id === investor.id ||
        c.investor_id === investor.investor_id
      );
      
      let parkName = contract?.park_name || investor.park_name || "N/A";
      if (parkName && parkName !== "N/A") {
        uniqueParksSet.add(parkName);
      }
      
      let status = investor.status || contract?.status || "Active";
      if (status && status !== "N/A") {
        uniqueStatusesSet.add(status);
      }
      
      let investmentType = investor.investment_type || contract?.investment_type || "N/A";
      
      const paymentMode = contract?.payment_mode || "USD";
      
      return {
        ...investor,
        park_name: parkName,
        investment_type: investmentType,
        investment_category: investmentType,
        year: contract?.year || (contract?.contract_issued_on ? new Date(contract.contract_issued_on).getFullYear().toString() : "2024"),
        month: contract?.month || (contract?.contract_issued_on ? (new Date(contract.contract_issued_on).getMonth() + 1).toString().padStart(2, '0') : "01"),
        monthly_rent: contract?.monthly_rent || 0,
        employee_capacity: contract?.employee_capacity || 0,
        export_capacity: contract?.export_capacity || "0%",
        status: status,
        capital: parseFloat(investor.capital) || 0,
        payment_mode: paymentMode,
        contract_issued_on: contract?.contract_issued_on || investor.contract_issued_on || null,
        investor_id: investor.id || investor.investor_id,
        nationality_origin: investor.nationality_origin || "N/A"
      };
    });
    
    return { transformedInvestors, uniqueParksSet, uniqueStatusesSet };
  };

  // Load data from API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const investorData = await GetDataFromApiWithParams(`investors/getinverstor`);
        const contractData = await GetDataFromApiWithParams(`agreements/allagreements`);
        
        console.log("Investor Response:", investorData);
        console.log("Contract Response:", contractData);
        
        let investors = [];
        let contracts = [];
        
        if (Array.isArray(investorData)) {
          investors = investorData;
        } else if (investorData?.data && Array.isArray(investorData.data)) {
          investors = investorData.data;
        } else if (investorData?.results && Array.isArray(investorData.results)) {
          investors = investorData.results;
        } else if (investorData?.investors && Array.isArray(investorData.investors)) {
          investors = investorData.investors;
        }
        
        if (Array.isArray(contractData)) {
          contracts = contractData;
        } else if (contractData?.data && Array.isArray(contractData.data)) {
          contracts = contractData.data;
        } else if (contractData?.results && Array.isArray(contractData.results)) {
          contracts = contractData.results;
        } else if (contractData?.contracts && Array.isArray(contractData.contracts)) {
          contracts = contractData.contracts;
        }
        
        console.log("Processed investors:", investors.length);
        console.log("Processed contracts:", contracts.length);
        
        const { transformedContracts, uniqueParksSet: contractParks, uniqueInvestmentCategoriesSet: contractCategories, uniqueStatusesSet: contractStatuses, uniqueRentTypesSet } = transformContractData(contracts, investors);
        const { transformedInvestors, uniqueParksSet: investorParks, uniqueStatusesSet: investorStatuses } = transformInvestorData(investors, contracts);
        
        setContractsData(transformedContracts);
        setFilteredContracts(transformedContracts);
        setInvestorsData(transformedInvestors);
        setFilteredInvestors(transformedInvestors);
        
        const allParks = new Set([...contractParks, ...investorParks]);
        setParksList(["all", ...Array.from(allParks).sort()]);
        
        const allInvestmentCategories = new Set([...contractCategories]);
        setInvestmentCategoriesList(["all", ...Array.from(allInvestmentCategories).sort()]);
        
        const allStatuses = new Set([...contractStatuses]);
        setStatusesList(["all", ...Array.from(allStatuses).sort()]);
        
        // NEW: Set rent types list from unique ownership_type values
        setRentTypesList(["all", ...Array.from(uniqueRentTypesSet).sort()]);
        
        console.log("Unique investment categories found:", Array.from(allInvestmentCategories));
        console.log("Unique statuses found:", Array.from(allStatuses));
        console.log("Unique parks found:", Array.from(allParks));
        console.log("Unique rent types found:", Array.from(uniqueRentTypesSet));
        
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Calculate summary statistics with USD and ETB separation
  const summaryStats = useMemo(() => {
    const activeContracts = contractsData.filter(c => c.status?.toLowerCase() === 'active').length;
    const activeInvestors = investorsData.filter(i => i.status?.toLowerCase() === 'active').length;
    
    const fdiCount = investorsData.filter(i => 
      i.nationality_origin && 
      i.nationality_origin.toLowerCase() !== 'ethiopia'
    ).length;

    const domesticCount = investorsData.filter(i => 
      i.nationality_origin && 
      i.nationality_origin.toLowerCase() === 'ethiopia'
    ).length;
    
    const jointVentureCount = contractsData.filter(c => c.investment_type === 'Joint Venture').length;
    const foreignInvestors = investorsData.filter(i => 
      i.nationality_origin && i.nationality_origin.toLowerCase() !== 'ethiopia'
    ).length;
    const localInvestors = investorsData.filter(i => 
      i.nationality_origin && i.nationality_origin.toLowerCase() === 'ethiopia'
    ).length;
    
    const usdCapital = investorsData
      .filter(i => normalizeCurrency(i.payment_mode) === 'usd')
      .reduce((sum, i) => sum + (i.capital || 0), 0);
    
    const etbCapital = investorsData
      .filter(i => normalizeCurrency(i.payment_mode) === 'etb')
      .reduce((sum, i) => sum + (i.capital || 0), 0);
    
    return {
      activeContracts, 
      activeInvestors, 
      fdiCount, 
      jointVentureCount, 
      domesticCount,
      totalContracts: contractsData.length, 
      foreignInvestors, 
      localInvestors,
      totalInvestors: investorsData.length, 
      totalCapitalUSD: usdCapital,
      totalCapitalETB: etbCapital
    };
  }, [contractsData, investorsData]);

  // Filter Data - UPDATED to include rent type filter
  const applyFilters = useCallback(() => {
    let filteredContractsList = [...contractsData];
    
    if (searchTerm) {
      filteredContractsList = filteredContractsList.filter(item =>
        item.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.tenant_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.park_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedPark !== "all") {
      filteredContractsList = filteredContractsList.filter(item => item.park_name === selectedPark);
    }
    
    if (selectedInvestmentCategory !== "all") {
      filteredContractsList = filteredContractsList.filter(item => 
        item.investment_category === selectedInvestmentCategory
      );
    }
    
    if (selectedStatus !== "all") {
      filteredContractsList = filteredContractsList.filter(item => 
        item.status === selectedStatus
      );
    }
    
    // NEW: Filter by rent type (ownership_type)
    if (selectedRentType !== "all") {
      filteredContractsList = filteredContractsList.filter(item => 
        item.ownership_type === selectedRentType
      );
    }
    
    if (startYear !== "all" || endYear !== "all") {
      filteredContractsList = filteredContractsList.filter(item => {
        const itemYear = parseInt(item.year);
        const startYearNum = startYear !== "all" ? parseInt(startYear) : -Infinity;
        const endYearNum = endYear !== "all" ? parseInt(endYear) : Infinity;
        return itemYear >= startYearNum && itemYear <= endYearNum;
      });
    }
    
    if (startMonth !== "all" || endMonth !== "all") {
      filteredContractsList = filteredContractsList.filter(item => {
        const itemMonth = parseInt(item.month);
        const startMonthNum = startMonth !== "all" ? parseInt(startMonth) : 1;
        const endMonthNum = endMonth !== "all" ? parseInt(endMonth) : 12;
        return itemMonth >= startMonthNum && itemMonth <= endMonthNum;
      });
    }
    
    setFilteredContracts(filteredContractsList);
    
    let filteredInvestorsList = [...investorsData];
    
    if (searchTerm) {
      filteredInvestorsList = filteredInvestorsList.filter(item =>
        item.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.contact_person?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedPark !== "all") {
      filteredInvestorsList = filteredInvestorsList.filter(item => item.park_name === selectedPark);
    }
    
    if (selectedInvestmentCategory !== "all") {
      filteredInvestorsList = filteredInvestorsList.filter(item => 
        item.investment_type === selectedInvestmentCategory
      );
    }
    
    if (selectedStatus !== "all") {
      filteredInvestorsList = filteredInvestorsList.filter(item => 
        item.status === selectedStatus
      );
    }
    
    if (startYear !== "all" || endYear !== "all") {
      filteredInvestorsList = filteredInvestorsList.filter(item => {
        const itemYear = parseInt(item.year);
        const startYearNum = startYear !== "all" ? parseInt(startYear) : -Infinity;
        const endYearNum = endYear !== "all" ? parseInt(endYear) : Infinity;
        return itemYear >= startYearNum && itemYear <= endYearNum;
      });
    }
    
    if (startMonth !== "all" || endMonth !== "all") {
      filteredInvestorsList = filteredInvestorsList.filter(item => {
        const itemMonth = parseInt(item.month);
        const startMonthNum = startMonth !== "all" ? parseInt(startMonth) : 1;
        const endMonthNum = endMonth !== "all" ? parseInt(endMonth) : 12;
        return itemMonth >= startMonthNum && itemMonth <= endMonthNum;
      });
    }
    
    setFilteredInvestors(filteredInvestorsList);
    setPage(0);
  }, [contractsData, investorsData, searchTerm, selectedPark, selectedInvestmentCategory, selectedStatus, selectedRentType, startYear, endYear, startMonth, endMonth]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Chart data preparation with USD and ETB separation
  const chartData = useMemo(() => {
    const parkContractData = {};
    const parkInvestorData = {};
    
    // Process contracts for park data
    filteredContracts.forEach(contract => {
      const park = contract.park_name || "Unassigned";
      if (!parkContractData[park]) {
        parkContractData[park] = { 
          totalContracts: 0, 
          fdiCount: 0,
          localCount: 0,
          jointVentureCount: 0, 
          activeCount: 0 
        };
      }
      parkContractData[park].totalContracts++;
      
      if (contract.nationality_origin && contract.nationality_origin.toLowerCase() !== 'ethiopia') {
        parkContractData[park].fdiCount++;
      } else if (contract.nationality_origin && contract.nationality_origin.toLowerCase() === 'ethiopia') {
        parkContractData[park].localCount++;
      }
      
      if (contract.status?.toLowerCase() === 'active') {
        parkContractData[park].activeCount++;
      }
    });
    
    // Process investors for park data with USD/ETB separation
    filteredInvestors.forEach(investor => {
      const park = investor.park_name || "Unassigned";
      if (!parkInvestorData[park]) { 
        parkInvestorData[park] = { 
          totalInvestors: 0, 
          foreignCount: 0,
          localCount: 0,
          totalCapitalUSD: 0,
          totalCapitalETB: 0
        };
      }
      parkInvestorData[park].totalInvestors++;
      
      const capital = investor.capital || 0;
      const paymentMode = normalizeCurrency(investor.payment_mode);
      
      if (investor.nationality_origin && investor.nationality_origin.toLowerCase() !== 'ethiopia') {
        parkInvestorData[park].foreignCount++;
      } else if (investor.nationality_origin && investor.nationality_origin.toLowerCase() === 'ethiopia') {
        parkInvestorData[park].localCount++;
      }
      
      if (paymentMode === 'usd') {
        parkInvestorData[park].totalCapitalUSD += capital;
      } else if (paymentMode === 'etb') {
        parkInvestorData[park].totalCapitalETB += capital;
      }
    });
    
    // Yearly contract data
    const yearlyContractData = {};
    filteredContracts.forEach(contract => {
      const year = contract.year;
      if (!yearlyContractData[year]) {
        yearlyContractData[year] = { 
          totalContracts: 0, 
          fdiCount: 0,
          localCount: 0,
          jointVentureCount: 0 
        };
      }
      yearlyContractData[year].totalContracts++;
      
      if (contract.nationality_origin && contract.nationality_origin.toLowerCase() !== 'ethiopia') {
        yearlyContractData[year].fdiCount++;
      } else if (contract.nationality_origin && contract.nationality_origin.toLowerCase() === 'ethiopia') {
        yearlyContractData[year].localCount++;
      }
    });
    
    // Yearly investor data with USD/ETB separation
    const yearlyInvestorData = {};
    filteredInvestors.forEach(investor => {
      const year = investor.year;
      if (!yearlyInvestorData[year]) {
        yearlyInvestorData[year] = { 
          totalInvestors: 0, 
          foreignCount: 0, 
          localCount: 0, 
          totalCapitalUSD: 0,
          totalCapitalETB: 0
        };
      }
      yearlyInvestorData[year].totalInvestors++;
      
      const capital = investor.capital || 0;
      const paymentMode = normalizeCurrency(investor.payment_mode);
      
      if (investor.nationality_origin && investor.nationality_origin.toLowerCase() !== 'ethiopia') {
        yearlyInvestorData[year].foreignCount++;
      } else if (investor.nationality_origin && investor.nationality_origin.toLowerCase() === 'ethiopia') {
        yearlyInvestorData[year].localCount++;
      }
      
      if (paymentMode === 'usd') {
        yearlyInvestorData[year].totalCapitalUSD += capital;
      } else if (paymentMode === 'etb') {
        yearlyInvestorData[year].totalCapitalETB += capital;
      }
    });
    
    return { parkContractData, parkInvestorData, yearlyContractData, yearlyInvestorData };
  }, [filteredContracts, filteredInvestors]);

  // Get yearly data for selected park
  const getYearlyDataForSelectedPark = (parkName) => {
    const parkYearlyData = {};
    
    if (parkName === "all") {
      return chartData.yearlyContractData;
    }
    
    filteredContracts.forEach(contract => {
      if (contract.park_name === parkName) {
        const year = contract.year;
        if (!parkYearlyData[year]) {
          parkYearlyData[year] = { 
            totalContracts: 0, 
            fdiCount: 0,
            localCount: 0
          };
        }
        parkYearlyData[year].totalContracts++;
        
        if (contract.nationality_origin && contract.nationality_origin.toLowerCase() !== 'ethiopia') {
          parkYearlyData[year].fdiCount++;
        } else if (contract.nationality_origin && contract.nationality_origin.toLowerCase() === 'ethiopia') {
          parkYearlyData[year].localCount++;
        }
      }
    });
    
    return parkYearlyData;
  };

  const yearlyDisplayData = useMemo(() => {
    if (selectedParkForYearly !== "all") {
      return getYearlyDataForSelectedPark(selectedParkForYearly);
    }
    return chartData.yearlyContractData;
  }, [selectedParkForYearly, chartData.yearlyContractData, filteredContracts, filteredInvestors]);

  // Reset Filters - UPDATED to include rent type
  const resetFilters = () => {
    setSearchTerm("");
    setSelectedPark("all");
    setSelectedInvestmentCategory("all");
    setSelectedStatus("all");
    setSelectedRentType("all");
    setStartYear("all");
    setEndYear("all");
    setStartMonth("all");
    setEndMonth("all");
  };

  // Export to CSV
  const handleExportCSV = () => {
    let headers = [], data = [];
    if (tabValue === 0) {
      headers = ['Company Name', 'Park Name', 'Investment Type', 'Status', 'Rent Type', 'Monthly Rent', 'Employees', 'Export Capacity', 'Year', 'Month'];
      data = filteredContracts.map(item => [item.company_name, item.park_name, item.investment_type, item.status, item.ownership_type || "N/A", item.monthly_rent, item.employee_capacity, item.export_capacity, item.year, item.month]);
    } else {
      headers = ['Investor ID', 'Company Name', 'Park Name', 'Investment Category', 'Nationality', 'Capital (USD)', 'Capital (ETB)', 'Payment Mode', 'Status', 'Contact Person', 'Phone No'];
      data = filteredInvestors.map(item => [
        item.investor_id, 
        item.company_name, 
        item.park_name, 
        item.investment_type,
        item.nationality_origin, 
        normalizeCurrency(item.payment_mode) === 'usd' ? formatCurrencyUSD(item.capital) : '-',
        normalizeCurrency(item.payment_mode) === 'etb' ? formatCurrencyETB(item.capital) : '-',
        item.payment_mode || 'USD',
        item.status, 
        item.contact_person, 
        item.phone_no
      ]);
    }
    const csvContent = [headers, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `investment_report_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleChangePage = (event, newPage) => { setPage(newPage); };
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };
  const getCurrentPageData = () => { const data = tabValue === 0 ? filteredContracts : filteredInvestors; return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage); };
  const handleViewDetails = (item, type) => { setSelectedItem(item); setDetailsType(type); setOpenDetailsDialog(true); };

  // Render park chart based on selected type
  const renderParkChart = (title, data, dataKey, color, formatValue) => {
    const hasValidData = Object.values(data).some(item => {
      const value = item[dataKey];
      return value && value > 0;
    });
    
    if (!hasValidData) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            No data available for {title}
          </Typography>
        </Box>
      );
    }
    
    if (parkChartType === 'pie') {
      const pieData = {};
      Object.entries(data).forEach(([key, val]) => { 
        const value = val[dataKey] || 0;
        if (value > 0) {
          pieData[key] = value;
        }
      });
      
      if (Object.keys(pieData).length === 0) {
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">No data available for {title}</Typography>
          </Box>
        );
      }
      return <PieChart title={title} data={pieData} formatValue={formatValue} />;
    } else {
      return <ParkBarChart title={title} parkData={data} dataKey={dataKey} color={color} formatValue={formatValue} />;
    }
  };

  // Render yearly chart based on selected type
  const renderYearlyChart = (title, data, dataKey, color, formatValue) => {
    const hasData = Object.values(data).some(yearData => (yearData[dataKey] || 0) > 0);
    
    if (!hasData) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">No data available for {title}</Typography>
        </Box>
      );
    }
    
    if (yearlyChartType === 'line') {
      return <YearlyLineChart title={title} yearlyData={data} dataKey={dataKey} color={color} formatValue={formatValue} />;
    } else {
      return <YearlyBarChart title={title} yearlyData={data} dataKey={dataKey} color={color} formatValue={formatValue} />;
    }
  };

  // Render capital chart (USD vs ETB together)
  const renderCapitalChart = (title, data) => {
    return <CapitalParkBarChart title={title} parkData={data} />;
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#f5f5f5', minHeight: '100vh', p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: '#1a237e', fontWeight: 'bold', textAlign: 'center', mb: 1 }}>
        Investment Report Dashboard
      </Typography>
      
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
        <Grid item xs={12} sm={6} md={2.4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '220px', minHeight: '85px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CardContent sx={{ textAlign: 'center', width: '100%', py: 0.75, px: 1, '&:last-child': { pb: 0.75 } }}>
              <DescriptionIcon sx={{ color: '#ed6c02', fontSize: 20, mb: 0.25 }} />
              <Typography variant="h6" sx={{  color: 'black', fontSize: '1.1rem', lineHeight: 1.2 }}>{summaryStats.totalContracts}</Typography>
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.65rem', display: 'block' }}>Total Contracts</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '220px', minHeight: '85px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CardContent sx={{ textAlign: 'center', width: '100%', py: 0.75, px: 1, '&:last-child': { pb: 0.75 } }}>
              <PeopleIcon sx={{ color: '#ed6c02', fontSize: 20, mb: 0.25 }} />
              <Typography variant="h6" sx={{ color: 'black', fontSize: '1.1rem', lineHeight: 1.2 }}>{summaryStats.totalInvestors}</Typography>
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.65rem', display: 'block' }}>Total Investors</Typography>
              <Typography variant="caption" sx={{ color: '#999', display: 'block', fontSize: '0.6rem', lineHeight: 1.1 }}>Active: {summaryStats.activeInvestors}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Card sx={{ bgcolor: 'white', boxShadow: 1, minHeight: '70px', width: '100%' }}>
                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 }, textAlign: 'center' }}>
                <AccountBalanceIcon sx={{ color: '#1976d2', fontSize: 18 }} />
                <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem', display: 'block' }}>
                    Total Capital
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, my: 0.5 }}>
                    <Typography variant="body2" sx={{ color: 'black', fontWeight: 'normal', fontSize: '0.8rem' }}>
                    USD: {formatCurrencyUSD(summaryStats.totalCapitalUSD)}
                    </Typography>
                    <Box sx={{ width: '1px', height: '16px', bgcolor: '#ddd' }} />
                    <Typography variant="body2" sx={{ color: 'black', fontWeight: 'normal', fontSize: '0.8rem' }}>
                    ETB: {formatCurrencyETB(summaryStats.totalCapitalETB)}
                    </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem', display: 'block', mt: 0.5 }}>
                    Across all parks
                </Typography>
                </CardContent>
            </Card>
            </Grid>
       
        <Grid item xs={12} sm={6} md={2.4} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, width: '100%', maxWidth: '220px', minHeight: '85px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CardContent sx={{ textAlign: 'center', width: '100%', py: 0.75, px: 1, '&:last-child': { pb: 0.75 } }}>
              <TrendingUpIcon sx={{ color: '#1565c0', fontSize: 20, mb: 0.25 }} />
              <Typography variant="h6" sx={{  color: 'black', fontSize: '1.1rem', lineHeight: 1.2 }}>{summaryStats.fdiCount}</Typography>
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.65rem', display: 'block' }}>FDI Contracts</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Filters Section */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={2.4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by company, park, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
                endAdornment: searchTerm && (<InputAdornment position="end"><ClearIcon sx={{ cursor: 'pointer' }} onClick={() => setSearchTerm("")} /></InputAdornment>)
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={1.8}>
            <FormControl fullWidth size="small">
              <InputLabel>Park</InputLabel>
              <Select value={selectedPark} onChange={(e) => setSelectedPark(e.target.value)} label="Park">
                {parksList.map((park) => (<MenuItem key={park} value={park}>{park === "all" ? "All Parks" : park}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={1.8}>
            <FormControl fullWidth size="small">
              <InputLabel>Investment Category</InputLabel>
              <Select value={selectedInvestmentCategory} onChange={(e) => setSelectedInvestmentCategory(e.target.value)} label="Investment Category">
                <MenuItem value="all">All Categories</MenuItem>
                {investmentCategoriesList.filter(cat => cat !== "all").map((category) => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={1.8}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} label="Status">
                {statusesList.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status === "all" ? "All Status" : status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* NEW: Rent Type Dropdown */}
          <Grid item xs={12} md={1.8}>
            <FormControl fullWidth size="small">
              <InputLabel>Rent Type</InputLabel>
              <Select value={selectedRentType} onChange={(e) => setSelectedRentType(e.target.value)} label="Rent Type">
                {rentTypesList.map((rentType) => (
                  <MenuItem key={rentType} value={rentType}>
                    {rentType === "all" ? "All Rent Types" : rentType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2.4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button fullWidth variant="outlined" onClick={resetFilters} startIcon={<ClearIcon />} size="small" sx={{ textTransform: 'none', borderColor: '#1976d2', color: '#1976d2' }}>Reset</Button>
              <Button fullWidth variant="contained" color="primary" size="small" onClick={handleExportCSV} startIcon={<DownloadIcon />} sx={{ textTransform: 'none' }}>Export</Button>
            </Box>
          </Grid>
        </Grid>
        
        {/* Year and Month Range Filters */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Year From</InputLabel>
              <Select value={startYear} onChange={(e) => setStartYear(e.target.value)} label="Year From">
                {years.map((year) => (<MenuItem key={year} value={year}>{year === "all" ? "All Years" : year}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Year To</InputLabel>
              <Select value={endYear} onChange={(e) => setEndYear(e.target.value)} label="Year To">
                {years.map((year) => (<MenuItem key={year} value={year}>{year === "all" ? "All Years" : year}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Month From</InputLabel>
              <Select value={startMonth} onChange={(e) => setStartMonth(e.target.value)} label="Month From">
                {months.map((month) => (<MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Month To</InputLabel>
              <Select value={endMonth} onChange={(e) => setEndMonth(e.target.value)} label="Month To">
                {months.map((month) => (<MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="textSecondary">Chart:</Typography>
              <ToggleButtonGroup value={showChart ? (chartView === 'park' ? parkChartType : yearlyChartType) : null} exclusive onChange={(e, val) => { if (showChart && val) { if (chartView === 'park') setParkChartType(val); else setYearlyChartType(val); } }} size="small">
                <ToggleButton value="bar"><Tooltip title="Bar Chart"><BarChartIcon fontSize="small" /></Tooltip></ToggleButton>
                <ToggleButton value="pie" sx={{ display: chartView === 'park' ? 'inline-flex' : 'none' }}><Tooltip title="Pie Chart"><PieChartIcon fontSize="small" /></Tooltip></ToggleButton>
                <ToggleButton value="line" sx={{ display: chartView === 'yearly' ? 'inline-flex' : 'none' }}><Tooltip title="Line Chart"><ShowChartIcon fontSize="small" /></Tooltip></ToggleButton>
              </ToggleButtonGroup>
              <Button variant="outlined" size="small" onClick={() => setShowChart(!showChart)} sx={{ minWidth: '120px', textTransform: 'none', borderColor: '#1976d2', color: '#1976d2' }}>{showChart ? "Hide Chart" : "Show Chart"}</Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Conditional Rendering: Show Chart OR Table */}
      {showChart ? (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button variant={chartView === 'park' ? 'contained' : 'outlined'} size="small" onClick={() => setChartView('park')} sx={{ textTransform: 'none', minWidth: '120px' }}><LocationOnIcon sx={{ mr: 1, fontSize: 18 }} />Park-wise View</Button>
            <Button variant={chartView === 'yearly' ? 'contained' : 'outlined'} size="small" onClick={() => setChartView('yearly')} sx={{ textTransform: 'none', minWidth: '120px' }}><CalendarIcon sx={{ mr: 1, fontSize: 18 }} />Yearly Trend</Button>
          </Box>
          
          <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            {chartView === 'park' ? (
              <>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="textSecondary">Chart Type:</Typography>
                    <ToggleButtonGroup value={parkChartType} exclusive onChange={(e, val) => val && setParkChartType(val)} size="small">
                      <ToggleButton value="bar"><Tooltip title="Bar Chart"><BarChartIcon fontSize="small" /></Tooltip></ToggleButton>
                      <ToggleButton value="pie"><Tooltip title="Pie Chart"><PieChartIcon fontSize="small" /></Tooltip></ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>
                
                {/* Tabs for Park-Wise Charts */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={parkChartTab} onChange={(e, newValue) => setParkChartTab(newValue)}>
                    <Tab label="Contracts by Park" />
                    <Tab label="Local Investors by Park" />
                    <Tab label="FDI Investment by Park" />
                    <Tab label="Capital Investment by Park" />
                  </Tabs>
                </Box>
                
                <Box sx={{ mt: 2, minHeight: 450 }}>
                  <TabPanel value={parkChartTab} index={0}>
                    {renderParkChart("Contracts by Park", chartData.parkContractData, "totalContracts", "#1976d2", (v) => formatNumber(v))}
                  </TabPanel>
                  <TabPanel value={parkChartTab} index={1}>
                    {renderParkChart("Local Investors by Park", chartData.parkInvestorData, "localCount", "#2e7d32", (v) => formatNumber(v))}
                  </TabPanel>
                  <TabPanel value={parkChartTab} index={2}>
                    {renderParkChart("FDI Investment by Park", chartData.parkContractData, "fdiCount", "#1565c0", (v) => formatNumber(v))}
                  </TabPanel>
                  <TabPanel value={parkChartTab} index={3}>
                    {renderCapitalChart("Capital Investment by Park (USD vs ETB)", chartData.parkInvestorData)}
                  </TabPanel>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2} alignItems="center" justifyContent="center">
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select Park</InputLabel>
                        <Select value={selectedParkForYearly} onChange={(e) => setSelectedParkForYearly(e.target.value)} label="Select Park">
                          <MenuItem value="all">All Parks (Summary)</MenuItem>
                          {parksList.filter(p => p !== "all").map((park) => (<MenuItem key={park} value={park}>{park}</MenuItem>))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="textSecondary">Chart Type:</Typography>
                        <ToggleButtonGroup value={yearlyChartType} exclusive onChange={(e, val) => val && setYearlyChartType(val)} size="small">
                          <ToggleButton value="line"><Tooltip title="Line Chart"><ShowChartIcon fontSize="small" /></Tooltip></ToggleButton>
                          <ToggleButton value="bar"><Tooltip title="Bar Chart"><BarChartIcon fontSize="small" /></Tooltip></ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                
                {/* Tabs for Yearly Trends */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs value={yearlyChartTab} onChange={(e, newValue) => setYearlyChartTab(newValue)}>
                    <Tab label="Contract Trends" />
                    <Tab label="FDI Trends" />
                    <Tab label="Domestic Trends" />
                    <Tab label="Capital Trends" />
                  </Tabs>
                </Box>

                <Box sx={{ mt: 2, minHeight: 450 }}>
                  <TabPanel value={yearlyChartTab} index={0}>
                    {renderYearlyChart(`Yearly Contract Trends${selectedParkForYearly !== "all" ? ` - ${selectedParkForYearly}` : ""}`, yearlyDisplayData, "totalContracts", "#1976d2", (v) => formatNumber(v))}
                  </TabPanel>
                  <TabPanel value={yearlyChartTab} index={1}>
                    {renderYearlyChart(`Yearly FDI Trends${selectedParkForYearly !== "all" ? ` - ${selectedParkForYearly}` : ""}`, yearlyDisplayData, "fdiCount", "#1565c0", (v) => formatNumber(v))}
                  </TabPanel>
                  <TabPanel value={yearlyChartTab} index={2}>
                    {renderYearlyChart(`Yearly Domestic Trends${selectedParkForYearly !== "all" ? ` - ${selectedParkForYearly}` : ""}`, yearlyDisplayData, "localCount", "#2e7d32", (v) => formatNumber(v))}
                  </TabPanel>
                  <TabPanel value={yearlyChartTab} index={3}>
                    <YearlyCombinedCapitalChart 
                      title={`Yearly Capital Trends${selectedParkForYearly !== "all" ? ` - ${selectedParkForYearly}` : ""}`} 
                      yearlyData={chartData.yearlyInvestorData} 
                    />
                  </TabPanel>
                </Box>
              </>
            )}
          </Paper>
        </>
      ) : (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#fafafa' }}>
            <Tab label="Contracts" icon={<DescriptionIcon />} iconPosition="start" />
            <Tab label="Investors" icon={<BusinessIcon />} iconPosition="start" />
          </Tabs>
          
          <TabPanel value={tabValue} index={0}>
            {loading ? (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}><CircularProgress /><Typography sx={{ ml: 2 }}>Loading contracts data...</Typography></Box>) : filteredContracts.length === 0 ? (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}><Typography sx={{ color: 'text.secondary' }}>No contracts data available</Typography></Box>) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Company Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Park Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Investment Type</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Rent Type</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Monthly Rent</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Employees</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Export Capacity</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getCurrentPageData().map((row) => {
                        if (!row) return null;
                        return (
                          <TableRow key={row.id || row.tenant_id} hover>
                            <TableCell>{row.company_name}</TableCell>
                            <TableCell>{row.park_name}</TableCell>
                            <TableCell>{row.investment_type}</TableCell>
                            <TableCell>{row.status} </TableCell>
                            <TableCell>{row.ownership_type || "N/A"}</TableCell>
                            <TableCell align="right">{formatCurrencyUSD(row.monthly_rent)}</TableCell>
                            <TableCell align="right">{formatNumber(row.employee_capacity)}</TableCell>
                            <TableCell>{row.export_capacity}</TableCell>
                            <TableCell align="center">
                              <Tooltip title="View Details">
                                <IconButton size="small" onClick={() => handleViewDetails(row, 'contract')}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={filteredContracts.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
              </>
            )}
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            {loading ? (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}><CircularProgress /><Typography sx={{ ml: 2 }}>Loading investors data...</Typography></Box>) : filteredInvestors.length === 0 ? (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}><Typography sx={{ color: 'text.secondary' }}>No investors data available</Typography></Box>) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Investor ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Company Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Park Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Investment Category</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Nationality</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Capital (USD)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Capital (ETB)</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Payment Mode</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Contact Person</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Phone No</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Issue Date</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                    { 
                      getCurrentPageData().map((row) => {
                        if (!row) return null;
                        return (
                          <TableRow key={row.id || row.investor_id} hover>
                            <TableCell>{row.investor_id}</TableCell>
                            <TableCell sx={{ fontWeight: 'medium' }}>{row.company_name}</TableCell>
                            <TableCell>{row.park_name}</TableCell>
                            <TableCell>{row.investment_type} </TableCell>
                            <TableCell>{row.nationality_origin}</TableCell>
                            <TableCell align="right" sx={{ color: '#1976d2' }}>{normalizeCurrency(row.payment_mode) === 'usd' ? formatCurrencyUSD(row.capital) : '-'}</TableCell>
                            <TableCell align="right" sx={{ color: '#ed6c02' }}>{normalizeCurrency(row.payment_mode) === 'etb' ? formatCurrencyETB(row.capital) : '-'}</TableCell>
                            <TableCell>{row.payment_mode || 'USD'} </TableCell>
                            <TableCell>{row.status} </TableCell>
                            <TableCell>{row.contact_person || "N/A"}</TableCell>
                            <TableCell>{row.phone_no || row.mobile_no || "N/A"}</TableCell>
                            <TableCell align="center">
                              {row.contract_issued_on ? new Date(row.contract_issued_on).toLocaleDateString() : "N/A"}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="View Details">
                                <IconButton size="small" onClick={() => handleViewDetails(row, 'investor')}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={filteredInvestors.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
              </>
            )}
          </TabPanel>
        </Paper>
      )}
      
      {/* Details Dialog */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
              {detailsType === 'contract' ? 'Contract Details' : 'Investor Details'}
            </Typography>
            <IconButton onClick={() => setOpenDetailsDialog(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedItem && (
            <Box sx={{ mt: 2 }}>
              {detailsType === 'contract' ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Company Name</Typography><Typography variant="body1" sx={{ fontWeight: 'medium' }}>{selectedItem.company_name}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Park Name</Typography><Typography variant="body1">{selectedItem.park_name}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Investment Type</Typography><Typography variant="body1">{selectedItem.investment_type}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Status</Typography><Chip label={selectedItem.status} size="small" sx={getStatusColor(selectedItem.status)} /></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Rent Type</Typography><Typography variant="body1">{selectedItem.ownership_type || "N/A"}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Monthly Rent</Typography><Typography variant="body1" sx={{ color: '#2e7d32' }}>{formatCurrencyUSD(selectedItem.monthly_rent)}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Employee Capacity</Typography><Typography variant="body1">{formatNumber(selectedItem.employee_capacity)}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Export Capacity</Typography><Typography variant="body1">{selectedItem.export_capacity}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Contract Period</Typography><Typography variant="body1">{selectedItem.contract_period_in_months} months</Typography></Grid>
                  <Grid item xs={12}><Typography variant="subtitle2" sx={{ color: '#666' }}>Description</Typography><Typography variant="body2">{selectedItem.description || "N/A"}</Typography></Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Investor ID</Typography><Typography variant="body1">{selectedItem.investor_id}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Company Name</Typography><Typography variant="body1" sx={{ fontWeight: 'medium' }}>{selectedItem.company_name}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Park Name</Typography><Typography variant="body1">{selectedItem.park_name}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Investment Category</Typography><Chip label={selectedItem.investment_type} size="small" sx={getInvestmentCategoryColor(selectedItem.investment_type)} /></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Nationality</Typography><Typography variant="body1">{selectedItem.nationality_origin}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Capital (USD)</Typography><Typography variant="body1" sx={{ color: '#1976d2' }}>{normalizeCurrency(selectedItem.payment_mode) === 'usd' ? formatCurrencyUSD(selectedItem.capital) : '-'}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Capital (ETB)</Typography><Typography variant="body1" sx={{ color: '#ed6c02' }}>{normalizeCurrency(selectedItem.payment_mode) === 'etb' ? formatCurrencyETB(selectedItem.capital) : '-'}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Payment Mode</Typography><Typography variant="body1"><Chip label={selectedItem.payment_mode || 'USD'} size="small" sx={{ bgcolor: selectedItem.payment_mode === 'USD' ? '#e3f2fd' : '#fff3e0', color: selectedItem.payment_mode === 'USD' ? '#1565c0' : '#ef6c00' }} /></Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Contact Person</Typography><Typography variant="body1">{selectedItem.contact_person || "N/A"}</Typography></Grid>
                  <Grid item xs={12} md={6}><Typography variant="subtitle2" sx={{ color: '#666' }}>Phone</Typography><Typography variant="body1">{selectedItem.phone_no || selectedItem.mobile_no || "N/A"}</Typography></Grid>
                  <Grid item xs={12}><Typography variant="subtitle2" sx={{ color: '#666' }}>Description</Typography><Typography variant="body2">{selectedItem.description || "N/A"}</Typography></Grid>
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => setOpenDetailsDialog(false)} variant="outlined">Close</Button>
        </Box>
      </Dialog>
    </Box>
  );
}